// Smart Sash Admin Panel - Permission Configuration
// Central source of truth for all frontend RBAC rules

import { UserRole } from '@/models';

// Role → permission definitions live in docs/roles.json (authoritative source is the backend JWT).

// ----- Route Access (permission-based) -----

/** Permission keys required to access each route (user needs at least one) */
export const routePermissions: Record<string, string[]> = {
  '/dashboard': [], // all authenticated users
  '/users': ['User.ViewAll'],
  '/devices': ['Device.ViewAll'],
  '/labs': ['Lab.ViewAll'],
  '/certifications': ['Certification.ViewAll'],
  '/rfid': ['Rfid.ViewAll', 'Rfid.ViewOwn'],
  '/admin/rfid-tags': ['Rfid.ViewAll'],
  '/streams': ['Streaming.View', 'Video.Observer'],
  '/logs': ['System.ViewLogs'],
  '/institutions': ['Institution.Manage'],
  '/groups': ['Group.Manage'],
  '/users/:userId/scopes': ['Scope.ViewAll', 'Scope.Assign'],
  '/users/:userId/permissions': ['FullAccess'],
  '/reports/hood-usage': ['FullAccess', 'Lab.ViewAll', 'Lab.View'],
};

// ----- Action Permissions -----

export type Action =
  | 'user:create'
  | 'user:edit'
  | 'user:delete'
  | 'user:toggle'
  | 'device:create'
  | 'device:edit'
  | 'device:delete'
  | 'lab:create'
  | 'lab:edit'
  | 'lab:delete'
  | 'institution:create'
  | 'institution:edit'
  | 'institution:delete'
  | 'group:create'
  | 'group:edit'
  | 'group:delete'
  | 'scope:view'
  | 'scope:edit'
  | 'certification:edit'
  | 'role:assign';

/** Maps legacy Action keys to backend permission keys */
export const actionToPermission: Record<Action, string> = {
  'user:create': 'User.Create',
  'user:edit': 'User.Update',
  'user:delete': 'User.Delete',
  'user:toggle': 'User.Update',
  'device:create': 'Device.Create',
  'device:edit': 'Device.Update',
  'device:delete': 'Device.Delete',
  'lab:create': 'Lab.Create',
  'lab:edit': 'Lab.Update',
  'lab:delete': 'Lab.Delete',
  'institution:create': 'Institution.Manage',
  'institution:edit': 'Institution.Manage',
  'institution:delete': 'Institution.Manage',
  'group:create': 'Group.Manage',
  'group:edit': 'Group.Manage',
  'group:delete': 'Group.Manage',
  'scope:view': 'Scope.ViewAll',
  'scope:edit': 'Scope.Assign',
  'certification:edit': 'Certification.Update',
  'role:assign': 'User.ManageRoles',
};

/**
 * Get the default route for a user based on their highest-priority role.
 * Used for redirecting unauthorized users.
 */
export function getDefaultRouteForRoles(roles: UserRole[]): string {
  if (
    roles.includes(UserRole.HawkinsAdmin) ||
    roles.includes(UserRole.InstitutionAdmin) ||
    roles.includes(UserRole.LabAdmin) ||
    roles.includes(UserRole.LabSupervisor)
  ) {
    return '/dashboard';
  }
  if (roles.includes(UserRole.HawkinsInstaller) || roles.includes(UserRole.SashUser)) {
    return '/dashboard';
  }
  return '/dashboard';
}
